import { TYPE_BUDGET_CHANGE, TYPE_EXPENSE_FUND, TYPE_POSITION_FUND, TYPE_PURCHASE_ORDER, TYPE_PURCHASE_REQUISITION } from '../myviews/queueTypes';

/**
 * The one place the five actionable types' payload shapes live.
 *
 * Approve-vs-reject is spelled three different ways by the endpoints this
 * reuses: a `status` constant for requisitions and expenditure approvals, a
 * boolean `isBudgetted` for the two fund types, and the literal strings
 * 'approved'/'rejected' for a budget change. The pop-up asks for "approve" or
 * "reject" and this translates; nothing above it should know which spelling a
 * type uses.
 *
 * These are the SAME endpoints the per-type module tabs post to. No parallel
 * actioning path exists, so each type's side effects — the requisition's
 * pr_number stamp and PR Approved email, the expense fund's EA Approved email on
 * the last approval — keep running exactly once, where they always did.
 */

const PLACEHOLDERS = {
    [TYPE_PURCHASE_REQUISITION]: 'REQUISITION_ID',
    [TYPE_PURCHASE_ORDER]: 'ORDER_ID',
    [TYPE_POSITION_FUND]: 'POSITION_FUND_APPROVAL_ID',
    [TYPE_EXPENSE_FUND]: 'EXPENSE_FUND_APPROVAL_ID',
    [TYPE_BUDGET_CHANGE]: 'BUDGET_CHANGE_ID',
};

/**
 * Builds the request for one action.
 *
 * @param {string} type      one of the four actionable queue types
 * @param {boolean} approve  true to approve, false to reject
 * @param {object} context   {row, comments, statuses, urls, additionalRecipients, totals}
 * @returns {{method: string, url: string, payload: object}}
 */
export default function approvalActionRequest(type, approve, context) {
    const { recordId, comments, statuses = {}, url, additionalRecipients = [], totals = {} } = context;

    const resolvedUrl = url.replace(PLACEHOLDERS[type], recordId);

    switch (type) {
        case TYPE_PURCHASE_REQUISITION:
        case TYPE_PURCHASE_ORDER: {
            const payload = {
                status: approve ? statuses.approved : statuses.rejected,
                comments,
            };

            // Only sent on approve, matching the modals this replaces: the
            // recipients picker chooses who receives the "approved" email, and a
            // rejection sends none.
            if (approve && additionalRecipients.length) {
                payload.additional_recipients = additionalRecipients;
            }

            return { method: 'put', url: resolvedUrl, payload };
        }

        case TYPE_POSITION_FUND:
        case TYPE_EXPENSE_FUND:
            return {
                method: 'patch',
                url: resolvedUrl,
                payload: {
                    isBudgetted: approve,
                    comments,
                    // The approver is deciding on these figures, and the server
                    // reads them to work out the contingency draw.
                    totalNew: totals.totalNew ?? 0,
                    totalOld: totals.totalOld ?? 0,
                },
            };

        case TYPE_BUDGET_CHANGE:
            return {
                method: 'put',
                url: resolvedUrl,
                payload: {
                    // Spelled out rather than sent as a boolean: the endpoint
                    // enumerates these two strings and refuses anything else, so
                    // a misspelling is a 422 instead of an approval that draws on
                    // contingency.
                    status: approve ? 'approved' : 'rejected',
                    comments,
                    // NO AMOUNTS. The source-of-funds split is system-calculated
                    // — local contingency first, then central, then event profit
                    // — so the approver decides yes or no and nothing else.
                },
            };

        default:
            throw new Error(`approvalActionRequest: ${type} cannot be actioned inline`);
    }
}
